import { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { AutoFixHigh, Check, Preview, HelpOutline, Close, ArrowDropDown, Clear } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { extractFieldsFromPattern, getAvailableFields, getFieldsInPattern } from '../utils/metadataParser';
import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import PatternPanelAlert from '../components/PatternPanelAlert';
import { useSectionStyles } from '@/styles/utilities/hooks';

const PatternExtractionPanel = forwardRef(({ 
  selectedRows = [], 
  onPatternApply,
  uploadFiles = [],
  selectedUploadRows = [],
  onPreviewUpdate,
  previewMode = false
}, ref) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  const [pattern, setPattern] = useState('');
  const [previewResults, setPreviewResults] = useState({});
  const [directValues, setDirectValues] = useState({});
  const [sampleSource, setSampleSource] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const patternInputRef = useRef(null);


  // Calculate total selected early so it can be used in useEffects
  const totalSelected = selectedRows.length + selectedUploadRows.length;
  
  // Create stable reference for selected row IDs
  const selectedRowIds = useMemo(() => 
    selectedRows.map(row => row.id).join(','), 
    [selectedRows]
  );
  
  // Pattern templates for autocomplete (simpler first)
  const patternTemplates = [
    '{ligand}',
    '{origin}_{ligand}',
    '{batch}/{ligand}',
    '{target}/{origin}_{ligand}',
    '{batch}/{origin}_{ligand}.pdb',
    '{target}/{origin}_{ligand}.{ext}',
    '{project}/{target}/{batch}/{origin}_{ligand}.{ext}',
    '{prepared}_{target}/{subgroup}/{origin}_{ligand}.{ext}',
    '{prepared}_{target}_{workflow}/{batch}/{origin}_{ligand}.{ext}',
  ];

  // Available field placeholders (column names -> API field mapping)
  const fieldPlaceholders = getAvailableFields();
  
  // Extract fields from pattern - defined early as it's used in useEffects
  const extractFieldsFromPattern = useCallback((fullPath, patternStr) => {
    if (!patternStr.trim()) return {};

    try {
      // Convert pattern to regex - be explicit about separators
      let regexPattern = patternStr;
      
      // First, escape special regex characters (except our placeholders and wildcards)
      // But don't escape the curly braces for placeholders
      regexPattern = regexPattern.replace(/([.+?^$()|[\]\\])/g, '\\$1');
      
      // Now handle our placeholders - match until we hit the next literal character
      // This is done by looking ahead to see what comes after the placeholder
      const placeholderRegex = /\{(\w+)\}([^{]*)/g;
      regexPattern = regexPattern.replace(placeholderRegex, (match, fieldName, followingText) => {
        if (fieldName === 'ext') {
          // Extension: match everything after the last dot
          return '(?<ext>[^.]+)' + followingText;
        }
        
        // For other fields, match based on what follows
        if (followingText) {
          // Get the first character of the following text to use as delimiter
          const delimiter = followingText[0];
          if (delimiter === '.') {
            // Before file extension - match everything except dot and slash (stay in same directory)
            return `(?<${fieldName}>[^/.]+)` + followingText;
          } else if (delimiter === '/') {
            // Explicit path separator - match everything except forward slash
            return `(?<${fieldName}>[^/]+)` + followingText;
          } else if (delimiter === '\\' && followingText[1] === '\\') {
            // Escaped backslash (Windows path separator) - match everything except backslash
            return `(?<${fieldName}>[^\\\\]+)` + followingText;
          } else if (delimiter === '_') {
            // Explicit underscore separator - match everything except underscore and slash
            return `(?<${fieldName}>[^/_]+)` + followingText;
          } else if (delimiter === '-') {
            // Explicit dash separator - match everything except dash and slash
            return `(?<${fieldName}>[^/-]+)` + followingText;
          } else {
            // Default: match until we hit the literal following text, but don't cross directories
            return `(?<${fieldName}>[^/]*?)` + followingText;
          }
        } else {
          // No following text (field is at the end) - match everything except directory separator
          return `(?<${fieldName}>[^/]+)`;
        }
      });
      
      // Handle wildcards
      regexPattern = regexPattern.replace(/\*/g, '[^/]*');   // * matches within directory
      regexPattern = regexPattern.replace(/\*\*/g, '.*');    // ** matches across directories
      
      const regex = new RegExp(regexPattern);
      const match = fullPath.match(regex);
      
      if (match && match.groups) {
        // Filter out 'ext' from results as it's not an updatable field
        const { ext, ...fields } = match.groups;
        return fields;
      }
    } catch (error) {
      console.warn('Pattern extraction failed:', error);
    }

    return {};
  }, []);
  
  // Map column names to API field names - defined early as it's used in useEffects
  const mapToAPIFields = useCallback((extractedData) => {
    const mapped = {};
    const fieldMap = {};
    
    // Create mapping from column names to API fields
    fieldPlaceholders.forEach(field => {
      fieldMap[field.name] = field.apiField;
    });
    
    // Map the extracted fields
    Object.entries(extractedData).forEach(([key, value]) => {
      const apiField = fieldMap[key] || key;
      
      // Special handling for date fields
      if (apiField === 'prepared_at' && value) {
        // Check if it's a 6-digit date (YYMMDD)
        if (/^\d{6}$/.test(value)) {
          // Convert YYMMDD to YYYY-MM-DD
          const yy = value.substring(0, 2);
          const mm = value.substring(2, 4);
          const dd = value.substring(4, 6);
          // Assume 20XX for years 00-50, 19XX for years 51-99
          const yyyy = parseInt(yy) <= 50 ? '20' + yy : '19' + yy;
          mapped[apiField] = `${yyyy}-${mm}-${dd}`;
        }
        // Check if it's an 8-digit date (YYYYMMDD)
        else if (/^\d{8}$/.test(value)) {
          // Convert YYYYMMDD to YYYY-MM-DD
          const yyyy = value.substring(0, 4);
          const mm = value.substring(4, 6);
          const dd = value.substring(6, 8);
          mapped[apiField] = `${yyyy}-${mm}-${dd}`;
        }
        // Check if it's already in ISO format (YYYY-MM-DD)
        else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          mapped[apiField] = value;
        }
        // For any other format, keep as is
        else {
          mapped[apiField] = value;
        }
      } else {
        mapped[apiField] = value;
      }
    });
    
    return mapped;
  }, []);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setPattern('');
      setPreviewResults({});
      setDirectValues({});
    },
    toggleHelp: () => {
      setShowHelp(prev => !prev);
    }
  }), []);
  
  // Update sample source when selection changes
  useEffect(() => {
    if (selectedRows.length > 0 && selectedRows[0]?.source_path) {
      setSampleSource(selectedRows[0].source_path);
    } else if (selectedUploadRows.length > 0 && selectedUploadRows[0]?.webkitRelativePath) {
      setSampleSource(selectedUploadRows[0].webkitRelativePath || selectedUploadRows[0].name);
    } else {
      // Clear sample source when no files selected
      setSampleSource('');
      setPreviewResults({});
    }
  }, [selectedRows, selectedUploadRows]);

  // Update preview when pattern or sample changes
  useEffect(() => {
    if (sampleSource && pattern) {
      const preview = extractFieldsFromPattern(sampleSource, pattern);
      setPreviewResults(preview);
    } else {
      setPreviewResults({});
      // Clear pattern if no sample source
      if (!sampleSource && pattern) {
        // Keep pattern but clear preview results only
        // This allows user to keep their pattern even when changing selection
      }
    }
  }, [sampleSource, pattern, extractFieldsFromPattern]);

  // Update table preview automatically when pattern changes
  useEffect(() => {
    if (!pattern || pattern.trim() === '') {
      // Clear preview when pattern is empty
      onPreviewUpdate && onPreviewUpdate(false, [], 'pattern');
    } else if (totalSelected > 0) {
      const extractedData = [];
      
      // Process selected prepared PDB files
      selectedRows.forEach(row => {
        const sourcePath = row.source_path || '';
        const extracted = extractFieldsFromPattern(sourcePath, pattern);
        const mapped = mapToAPIFields(extracted);
        
        // Merge with direct values
        const mappedDirectValues = mapToAPIFields(directValues);
        
        extractedData.push({
          id: row.id,
          ...mapped,
          ...mappedDirectValues
        });
      });
      
      // Process selected upload files
      selectedUploadRows.forEach(row => {
        const sourcePath = row.webkitRelativePath || row.name || '';
        const extracted = extractFieldsFromPattern(sourcePath, pattern);
        const mapped = mapToAPIFields(extracted);
        
        // Merge with direct values
        const mappedDirectValues = mapToAPIFields(directValues);
        
        extractedData.push({
          id: row.id,
          ...mapped,
          ...mappedDirectValues
        });
      });
      
      // Update parent with new extracted values, indicating source is 'pattern'
      onPreviewUpdate && onPreviewUpdate(true, extractedData, 'pattern');
    } else if (totalSelected === 0) {
      // Clear preview when no selection, but keep pattern for future use
      onPreviewUpdate && onPreviewUpdate(false, [], 'pattern');
    }
  }, [pattern, totalSelected, directValues, selectedRowIds, selectedUploadRows, onPreviewUpdate, extractFieldsFromPattern, mapToAPIFields]); // Include stable callbacks

  const handlePatternChange = (newPattern) => {
    setPattern(newPattern);
  };


  const toggleFieldInPattern = (fieldName) => {
    const placeholder = `{${fieldName}}`;
    const currentPattern = pattern || '';
    
    // Check if field is already in pattern
    if (fieldsInPattern.includes(fieldName)) {
      // Remove the field from pattern
      const regex = new RegExp(`\\{${fieldName}\\}`, 'g');
      const match = regex.exec(currentPattern);
      
      if (match) {
        const startIndex = match.index;
        const endIndex = startIndex + placeholder.length;
        const newPattern = currentPattern.slice(0, startIndex) + currentPattern.slice(endIndex);
        setPattern(newPattern);
        
        // If pattern becomes empty after removal, explicitly clear preview
        if (newPattern.trim() === '') {
          onPreviewUpdate && onPreviewUpdate(false, [], 'pattern');
        }
        
        // Set cursor position to where the field was removed
        setTimeout(() => {
          if (patternInputRef.current) {
            patternInputRef.current.focus();
            patternInputRef.current.setSelectionRange(startIndex, startIndex);
          }
        }, 0);
      }
    } else {
      // Add the field at the current cursor position
      let insertPosition = patternInputRef.current?.selectionStart || 0;
      
      // If pattern is empty, insert at beginning
      if (!currentPattern) {
        insertPosition = 0;
      }
      // Make sure position is within bounds
      insertPosition = Math.min(insertPosition, currentPattern.length);
      
      const newPattern = 
        currentPattern.slice(0, insertPosition) + 
        placeholder + 
        currentPattern.slice(insertPosition);
      
      setPattern(newPattern);
      
      // Set cursor position after the inserted field
      const newCursorPos = insertPosition + placeholder.length;
      setTimeout(() => {
        if (patternInputRef.current) {
          patternInputRef.current.focus();
          patternInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const fieldsInPattern = getFieldsInPattern(pattern);
  const missingFields = fieldPlaceholders.filter(field => 
    !fieldsInPattern.includes(field.name)
  );

  // Handle direct value changes for missing fields
  const handleDirectValueChange = (field, value) => {
    setDirectValues(prev => ({ ...prev, [field]: value }));
  };

  // Note: handleApplyPattern functionality moved to Save button in main grid header
  // Preview is now automatic when pattern is entered

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Help Dialog - Draggable */}
      <DraggableDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Pattern Extraction Help"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
        <Box>
          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Building Patterns:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Click any field chip to add it to your pattern. Click again to remove it.<br />
            • The pattern appears in the text field where you can also type directly.<br />
            • Your pattern will extract metadata from file paths automatically.
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Using Separators:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Add separators like <code>/</code>, <code>_</code>, or <code>-</code> between field placeholders to match your file structure.<br />
            • Example: <code>{`{target}_{origin}/{batch}/{ligand}.pdb`}</code><br />
            • The pattern must match the actual separators in your file paths.
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Placeholder Fields:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Use <code>{`{_}`}</code> or any custom name like <code>{`{1}`}</code>, <code>{`{skip}`}</code> for parts you want to ignore.<br />
            • These placeholders match text but don't extract it as metadata.<br />
            • Useful for consistent file naming where some parts aren't needed.
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Live Preview:
          </Typography>
          <Typography component="div" sx={{ mb: theme.spacing(2), fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Select rows in either data grid to see extraction results immediately.<br />
            • Extracted values appear next to field chips when they match.<br />
            • The main table shows preview of all extracted metadata.
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
            Applying Changes:
          </Typography>
          <Typography component="div" sx={{ fontSize: theme.typography.fontSize.sm, color: theme.palette.text.secondary }}>
            • Click the "Save" button in the main toolbar to apply extracted metadata.<br />
            • Changes will be saved to selected files in the database.<br />
            • Click "Cancel" to discard changes and clear the preview.
          </Typography>
        </Box>
      </DraggableDialog>
      
      {/* Selection Info - Always visible with consistent two-line layout */}
      <PatternPanelAlert 
        selectedRows={selectedRows}
        selectedUploadRows={selectedUploadRows}
      />
      
      {/* Scrollable Content Container */}
      <Box sx={{ flex: 1, overflow: 'auto', px: theme.spacing(2), pb: theme.spacing(2) }}>
        
        {/* EXTRACTION PATTERN Section */}
        <Box sx={{ mb: theme.spacing(2) }}>
          <Typography variant="caption" sx={{ ...sectionStyles.sectionHeader, mb: theme.spacing(1) }}>
            EXTRACTION PATTERN
          </Typography>
          
          {/* Pattern Input with Template Dropdown */}
          <Box sx={{ display: 'flex', gap: theme.spacing(0.5), mb: theme.spacing(1.5) }}>
            <TextField
              size="small"
              fullWidth
              value={pattern}
              onChange={(e) => handlePatternChange(e.target.value)}
              placeholder="e.g., {project}/{target}/{ligand}.{ext}"
              helperText="Use {field_name} placeholders to extract values from file paths"
              inputRef={patternInputRef}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: theme.typography.fontSize.base,
                  minHeight: theme.spacing(2.5)
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePatternChange('');
                      patternInputRef.current?.focus();
                    }}
                    sx={{ 
                      p: theme.spacing(0.25),
                      mr: theme.spacing(-0.5),
                      visibility: pattern ? 'visible' : 'hidden'
                    }}
                  >
                    <Clear sx={{ fontSize: theme.dimensions.icon.small }} />
                  </IconButton>
                )
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ 
                alignSelf: 'flex-start',
                mt: theme.spacing(0.25),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.spacing(0.5)
              }}
            >
              <ArrowDropDown />
            </IconButton>
          </Box>
          
          {/* Template Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {patternTemplates.map((template, index) => (
              <ListItemButton
                key={index}
                onClick={() => {
                  handlePatternChange(template);
                  setAnchorEl(null);
                  patternInputRef.current?.focus();
                }}
                sx={{ 
                  fontSize: theme.typography.fontSize.xs,
                  fontFamily: 'monospace',
                  py: theme.spacing(0.75)
                }}
              >
                <ListItemText 
                  primary={template}
                  primaryTypographyProps={{
                    sx: { 
                      fontSize: theme.typography.fontSize.xs,
                      fontFamily: 'monospace'
                    }
                  }}
                />
              </ListItemButton>
            ))}
          </Menu>
          
          {/* Field Chips with inline extracted values */}
          <Typography sx={{ ...sectionStyles.instructionText, mb: theme.spacing(0.75) }}>
            Click field to add to pattern:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
            {fieldPlaceholders.map(field => {
              const isInPattern = fieldsInPattern.includes(field.name);
              const extractedValue = previewResults[field.name];
              
              return (
                <Box key={field.name} sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Chip
                    label={field.name}
                    size="small"
                    variant={isInPattern ? "filled" : "outlined"}
                    color={isInPattern ? "primary" : "default"}
                    onClick={() => toggleFieldInPattern(field.name)}
                    sx={{ 
                      minWidth: theme.spacing(10),
                      fontWeight: isInPattern ? theme.typography.fontWeight.medium : theme.typography.fontWeight.regular,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isInPattern ? 'primary.dark' : 'action.hover'
                      }
                    }}
                  />
                  {isInPattern && extractedValue && (
                    <Typography
                      component="code"
                      sx={{
                        fontSize: theme.typography.fontSize.xs,
                        fontFamily: 'monospace',
                        color: theme.palette.success.dark,
                        backgroundColor: theme.palette.states.success,
                        px: theme.spacing(0.75),
                        py: theme.spacing(0.25),
                        borderRadius: theme.spacing(0.25),
                        fontWeight: theme.typography.fontWeight.medium
                      }}
                    >
                      {extractedValue}
                    </Typography>
                  )}
                  {isInPattern && !extractedValue && pattern && sampleSource && (
                    <Typography sx={sectionStyles.helperText}>
                      (no match)
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Sample Path for Testing */}
        {sampleSource && (
          <Box sx={{ mt: theme.spacing(1.5), mb: theme.spacing(2) }}>
            <Typography sx={{ ...sectionStyles.helperText, mb: theme.spacing(0.5) }}>
              Sample from selected file:
            </Typography>
            <Typography component="code" sx={sectionStyles.codeBlock}>
              {sampleSource}
            </Typography>
          </Box>
        )}

      </Box>
    </Box>
  );
});

PatternExtractionPanel.displayName = 'PatternExtractionPanel';

export default PatternExtractionPanel;